import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import moment, { MomentInput } from 'moment';

export interface IEventItemPayload {
  event_content: string;
  importance?: string;
  priority?: string;
}

export interface IMonthEvents {
  date: string;
  items: IEventItemPayload[];
}

export interface IEventRow {
  id: string;
  date: string;
  event_content: string;
  hasImportance: boolean;
}

/** Month bucket shape from LiveVisualization API: { date: 'YYYY-MM', items: [...] } */
interface IGroupedEventMonth {
  date: string;
  items?: IEventItemPayload[];
}

/** Flat row shape from overview / some APIs: { date: 'YYYY-MM-DD', event_content } */
interface IFlatEventRow {
  date?: string;
  event_content?: string;
  importance?: string;
  priority?: string;
}

/**
 * MonthPicker / range filters may pass a Moment, Date, or string.
 * Strict string formats alone can invalidate a Moment input — handle explicitly.
 */
export const parseMonth = (value?: MomentInput) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (moment.isMoment(value)) {
    const m = value.clone().startOf('month');
    return m.isValid() ? m : null;
  }
  if (value instanceof Date) {
    const m = moment(value).startOf('month');
    return m.isValid() ? m : null;
  }
  const strict = moment(value, ['YYYY-MM', 'YYYY-MM-DD', moment.ISO_8601], true);
  if (strict.isValid()) {
    return strict.startOf('month');
  }
  const loose = moment(value as string);
  return loose.isValid() ? loose.startOf('month') : null;
};

const toMonthKey = (dateStr: string): string | null => {
  const strict = moment(dateStr, ['YYYY-MM', 'YYYY-MM-DD', moment.ISO_8601], true);
  if (strict.isValid()) {
    return strict.format('YYYY-MM');
  }
  const loose = moment(dateStr);
  return loose.isValid() ? loose.format('YYYY-MM') : null;
};

const isGroupedEventsShape = (raw: unknown[]): raw is IGroupedEventMonth[] => {
  const first = raw[0] as IGroupedEventMonth | undefined;
  return Boolean(first && Array.isArray(first.items));
};

/**
 * Supports:
 * - Grouped: `[{ date: 'YYYY-MM', items: [{ event_content, importance }] }]`
 * - Flat: `[{ date: 'YYYY-MM-DD', event_content, importance?, priority? }]`
 */
export const normalizeEventList = (eventsData: any): IMonthEvents[] => {
  const raw = eventsData?.data?.events || eventsData?.events || [];
  if (!Array.isArray(raw) || raw.length === 0) {
    return [];
  }

  if (isGroupedEventsShape(raw)) {
    return raw
      .filter(item => item?.date)
      .map(item => {
        const key = toMonthKey(item.date);
        return {
          date: key || item.date,
          items: Array.isArray(item.items) ? item.items : [],
        };
      });
  }

  const monthMap = new Map<string, IEventItemPayload[]>();
  for (const item of raw as IFlatEventRow[]) {
    if (!item?.event_content || !item?.date) {
      continue;
    }
    const monthKey = toMonthKey(item.date);
    if (!monthKey) {
      continue;
    }
    const bucket = monthMap.get(monthKey) || [];
    bucket.push({
      event_content: item.event_content,
      importance: item.importance,
      priority: item.priority,
    });
    monthMap.set(monthKey, bucket);
  }

  return Array.from(monthMap.entries())
    .sort(
      (a, b) =>
        moment(a[0], 'YYYY-MM').valueOf() - moment(b[0], 'YYYY-MM').valueOf(),
    )
    .map(([date, items]) => ({ date, items }));
};

const rowHasImportance = (item: IEventItemPayload): boolean => {
  if (Boolean(item.importance?.trim())) {
    return true;
  }
  return String(item.priority || '').toLowerCase() === 'high';
};

export interface IUseEventsTimelineParams {
  eventsData?: any;
  selectedMonth?: MomentInput;
}

export interface IUseEventsTimelineResult {
  scrollRef: RefObject<HTMLDivElement>;
  setRowRef: (id: string) => (node: HTMLDivElement | null) => void;
  eventRows: IEventRow[];
  selectedTargetMonth: string;
  isHighlighted: (month: string) => boolean;
}

export const useEventsTimeline = ({
  eventsData,
  selectedMonth,
}: IUseEventsTimelineParams): IUseEventsTimelineResult => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const setRowRef = useCallback((id: string) => (node: HTMLDivElement | null) => {
    rowRefs.current[id] = node;
  }, []);

  const eventList = useMemo(() => normalizeEventList(eventsData), [eventsData]);

  const selectedEnd = parseMonth(selectedMonth);
  const selectedTargetMonth = selectedEnd ? selectedEnd.format('YYYY-MM') : '';

  const eventRows = useMemo<IEventRow[]>(() => {
    const rows = eventList.flatMap(monthItem => {
      const items = Array.isArray(monthItem.items) ? monthItem.items : [];
      if (items.length === 0) {
        return [];
      }
      return items.map((item, index) => ({
        id: `${monthItem.date}-${index}-${item.event_content || 'event'}`,
        date: monthItem.date,
        event_content: item.event_content,
        hasImportance: rowHasImportance(item),
      }));
    });

    return rows.sort(
      (a, b) =>
        moment(a.date, 'YYYY-MM').valueOf() - moment(b.date, 'YYYY-MM').valueOf(),
    );
  }, [eventList]);

  const eventMonthsSet = useMemo(() => {
    return new Set(eventRows.map(row => row.date));
  }, [eventRows]);

  const isHighlighted = useCallback(
    (month: string) => {
      if (!selectedTargetMonth) {
        return false;
      }
      if (!eventMonthsSet.has(selectedTargetMonth)) {
        return false;
      }
      return month === selectedTargetMonth;
    },
    [eventMonthsSet, selectedTargetMonth],
  );

  useEffect(() => {
    const node = scrollRef.current;
    if (!node || eventRows.length === 0 || !selectedTargetMonth) {
      return;
    }

    let targetRow = eventRows.find(row => row.date === selectedTargetMonth);
    if (!targetRow) {
      const targetTime = moment(selectedTargetMonth, 'YYYY-MM').valueOf();
      const previousRows = eventRows.filter(
        row => moment(row.date, 'YYYY-MM').valueOf() < targetTime,
      );
      targetRow = previousRows.length
        ? previousRows[previousRows.length - 1]
        : eventRows[0];
    }

    const target = targetRow ? rowRefs.current[targetRow.id] : null;
    if (!target) {
      return;
    }

    const targetTop =
      target.offsetTop - (node.clientHeight - target.clientHeight) / 2;
    const maxTop = Math.max(node.scrollHeight - node.clientHeight, 0);
    node.scrollTo({
      top: Math.min(Math.max(targetTop, 0), maxTop),
      behavior: 'smooth',
    });
  }, [eventRows, selectedTargetMonth]);

  return {
    scrollRef,
    setRowRef,
    eventRows,
    selectedTargetMonth,
    isHighlighted,
  };
};
